/**
 * Cheetah LZ17 in-house packing paper geometry (A4 host sheet).
 * Label sticky area + perforation from London Smoke & Cure stock template,
 * calibrated against physical print samples (perforation must clear the stub).
 *
 * A4: 210mm × 297mm
 * Label: 160mm wide × 105mm deep, 10mm from head, 25mm from right (= 25mm from left)
 */

export const CHEETAH_LZ17_PAPER = Object.freeze({
  id: 'cheetah-lz17',
  label: 'Cheetah LZ17 (in-house label sheet)',
  pageWidthMm: 210,
  pageHeightMm: 297,
  label: {
    widthMm: 160,
    heightMm: 105,
    /** Template: 10mm from head; content nudged down for breathing room */
    topMm: 10,
    leftMm: 25,
    rightMm: 25,
    /** Side/bottom inner pad */
    padMm: 3,
    /**
     * Extra top inset inside the label (~5px at 96dpi) so the first line
     * is not hard against the sticky top edge.
     */
    contentTopBreathingMm: 1.5,
  },
  /**
   * Physical perforation from print calibration (template text said 123mm;
   * samples showed stub content crossing that line — keep stub wholly below).
   */
  perforationFromTopMm: 138,
  /**
   * Clearance below the perforation before any stub ink.
   * Prices sit on the table edge that maps to the host top after -90°;
   * keep this generous so Unit/Line values do not kiss the physical perf.
   */
  stubPadTopMm: 12,
  stubPadSideMm: 6,
  stubPadBottomMm: 6,
  /** Inner padding inside the rotated stub content */
  stubInnerPadMm: 2.5,
  /** Stub content is rotated -90deg (CCW) so A5-portrait detail uses the long edge */
  stubRotationDeg: -90,
  /**
   * London Smoke & Cure logo — locked sizes.
   * Never stretch, crop, recolour, or shrink with density bands.
   * Always use `src/images/logo.png` (or the same asset via PACKING_SLIP_DEFAULT_LOGO_URL).
   */
  logo: {
    /** Outside sticky label */
    labelWidthMm: 26,
    /** Inside stub (classic OD header mark) */
    stubWidthMm: 24,
  },
});

/**
 * Locked readable density floor and weighted stub capacities.
 * Row units account for wrapped names, variations and KG annotations.
 */
export const CHEETAH_LZ17_PAGINATION = Object.freeze([
  Object.freeze({
    key: 'normal',
    firstCapacity: 8,
    continuationCapacity: 18,
    finalReserve: 4.5,
    metadataPenaltyScale: 1,
    fontPt: 9,
    lineHeightPx: 18,
  }),
  Object.freeze({
    key: 'compact',
    firstCapacity: 11,
    continuationCapacity: 24,
    finalReserve: 3.5,
    metadataPenaltyScale: 0.5,
    fontPt: 8,
    lineHeightPx: 15,
  }),
  Object.freeze({
    key: 'dense',
    firstCapacity: 20,
    continuationCapacity: 32,
    finalReserve: 3,
    metadataPenaltyScale: 0,
    fontPt: 7,
    lineHeightPx: 13,
  }),
]);

/** Available stub rectangle below the perforation (mm). */
export function getCheetahStubViewportMm(paper = CHEETAH_LZ17_PAPER) {
  const top = paper.perforationFromTopMm + paper.stubPadTopMm;
  const height = paper.pageHeightMm - top - paper.stubPadBottomMm;
  const left = paper.stubPadSideMm;
  const width = paper.pageWidthMm - paper.stubPadSideMm * 2;
  return { topMm: top, leftMm: left, widthMm: width, heightMm: Math.max(40, height) };
}

/**
 * Content split contract (outside label vs inside stub).
 * Used by builder + tests so the two faces stay distinct.
 */
export const CHEETAH_LZ17_CONTENT_SPLIT = Object.freeze({
  /** Stuck to the box — packers / cargo bike riders */
  labelIncludes: [
    'orderNumber',
    'qr',
    'shippingMethod',
    'deliveryDate',
    'dispatchDate',
    'deliveryWindow',
    'shipToAddress',
    'phone',
    'email',
    'deliveryNotes',
    'refrigerateCue',
    'packedReceivedSignatures',
    'companyNameBelowSignatures',
  ],
  labelExcludes: ['lineQuantities', 'linePrices', 'orderTotal', 'productTable', 'invoiceAddress'],
  /** Torn stub / inside — detail after 90° turn */
  /** Classic Order Desk packing-slip structure (no QR / window / storage banner). */
  stubIncludes: [
    'logo',
    'orderNumber',
    'date',
    'email',
    'phone',
    'shippingMethod',
    'checkoutDataLines',
    'invoiceAddress',
    'shipToAddress',
    'productTable',
    'lineQuantities',
    'linePrices',
    'orderTotal',
    'packedBySignature',
    'thankYouFooter',
  ],
  stubExcludes: ['qr', 'deliveryWindow', 'refrigerateCue', 'storageBanner'],
});

/** Density band for fitting long product lists on the stub. */
export function getCheetahStubDensity(itemCount) {
  const n = Number(itemCount) || 0;
  if (n >= 22) return 'xdense';
  if (n >= 14) return 'dense';
  return 'normal';
}

/**
 * Outside sticky-label fit bands. Prefer the most comfortable band that still
 * keeps every block (meta, address, notes, cold banner, signatures, company)
 * inside the physical 160×105 mm face with clear gaps — never overlap by
 * painting overflow on top of siblings.
 */
export const CHEETAH_LZ17_LABEL_FIT_BANDS = Object.freeze([
  Object.freeze({
    key: 'comfortable',
    basePt: 9,
    metaLineMm: 5.2,
    shipPt: 11,
    addrLineMm: 4.6,
    notesPt: 8,
    notesLineMm: 3.8,
    // Two-line chilled handoff banner needs more than a single-line strip.
    coldMm: 8.5,
    sigsMm: 9.5,
    gapMm: 1.6,
    qrMm: 16,
    companyMaxPt: 11,
    companyMinPt: 7.5,
  }),
  Object.freeze({
    key: 'compact',
    basePt: 8,
    metaLineMm: 4.2,
    shipPt: 9.5,
    addrLineMm: 3.8,
    notesPt: 7.5,
    notesLineMm: 3.3,
    coldMm: 7.5,
    sigsMm: 8.5,
    gapMm: 1.1,
    qrMm: 14,
    companyMaxPt: 10,
    companyMinPt: 7,
  }),
  Object.freeze({
    key: 'tight',
    basePt: 7.5,
    metaLineMm: 3.6,
    shipPt: 8.5,
    addrLineMm: 3.3,
    notesPt: 7,
    notesLineMm: 3,
    coldMm: 7,
    sigsMm: 7.5,
    gapMm: 0.8,
    qrMm: 12,
    companyMaxPt: 9,
    companyMinPt: 6.5,
  }),
  Object.freeze({
    key: 'micro',
    basePt: 7,
    metaLineMm: 3.2,
    shipPt: 8,
    addrLineMm: 3,
    notesPt: 6.5,
    notesLineMm: 2.7,
    coldMm: 6.5,
    sigsMm: 7,
    gapMm: 0.55,
    qrMm: 11,
    companyMaxPt: 8,
    companyMinPt: 6,
  }),
]);

const ptToMm = (pt) => Number(pt) * 0.352778;

/** Largest company point size that prefers one line, else two, within the label width. */
export function fitCheetahLabelCompanyPt(companyText, contentWidthMm, maxPt, minPt) {
  const text = String(companyText || '').trim();
  if (!text) return maxPt;
  const chars = Math.max(1, text.length);
  // Bold uppercase average advance ≈ 0.7em (conservative so long names shrink early).
  const usableMm = Math.max(40, Number(contentWidthMm) || 150) * 0.96;
  let pt = Number(maxPt);
  const floor = Number(minPt);

  const linesNeeded = (sizePt) => {
    const charMm = ptToMm(sizePt) * 0.7;
    const perLine = Math.max(8, Math.floor(usableMm / charMm));
    return Math.max(1, Math.ceil(chars / perLine));
  };

  // Prefer a single line whenever possible.
  while (pt > floor + 0.05) {
    if (linesNeeded(pt) <= 1) return Math.round(pt * 10) / 10;
    pt -= 0.5;
  }
  // Fall back to two lines at the floor size (still shrinks long names vs max).
  pt = Number(maxPt);
  while (pt > floor + 0.05) {
    if (linesNeeded(pt) <= 2) return Math.round(pt * 10) / 10;
    pt -= 0.5;
  }
  return Math.round(floor * 10) / 10;
}

function estimateNotesLines(notesCharCount, contentWidthMm, notesPt) {
  const chars = Math.max(0, Number(notesCharCount) || 0);
  if (!chars) return 0;
  const charMm = ptToMm(notesPt) * 0.5;
  const perLine = Math.max(12, Math.floor((contentWidthMm * 0.95) / charMm));
  // "Notes: " prefix ≈ 7 chars
  return Math.max(1, Math.ceil((chars + 7) / perLine));
}

function estimateCompanyLines(companyText, contentWidthMm, companyPt) {
  const text = String(companyText || '').trim();
  if (!text) return 0;
  const charMm = ptToMm(companyPt) * 0.7;
  const perLine = Math.max(8, Math.floor((contentWidthMm * 0.96) / charMm));
  return Math.max(1, Math.ceil(text.length / perLine));
}

/**
 * Pick the roomiest sticky-label density that fits the given content budget.
 * Logo width stays locked; QR and type scale with the band.
 */
export function resolveCheetahLabelFit(input = {}) {
  const paper = CHEETAH_LZ17_PAPER;
  const pad = paper.label.padMm;
  const topBreath = paper.label.contentTopBreathingMm || 0;
  const contentWidthMm =
    Number(input.contentWidthMm) || paper.label.widthMm - pad * 2;
  const contentHeightMm =
    Number(input.contentHeightMm) ||
    paper.label.heightMm - pad - pad - topBreath;

  const metaLineCount = Math.max(1, Number(input.metaLineCount) || 1);
  const addressLineCount = Math.max(1, Number(input.addressLineCount) || 1);
  const notesCharCount = Math.max(0, Number(input.notesCharCount) || 0);
  const companyText = String(input.companyText || '').trim();
  const hasCold = Boolean(input.hasCold);
  const hasSigs = input.hasSigs !== false;
  const hasQr = input.hasQr !== false;
  const logoWidthMm = paper.logo?.labelWidthMm || 26;

  for (const band of CHEETAH_LZ17_LABEL_FIT_BANDS) {
    const companyPt = fitCheetahLabelCompanyPt(
      companyText,
      contentWidthMm,
      band.companyMaxPt,
      band.companyMinPt,
    );
    const companyLines = estimateCompanyLines(companyText, contentWidthMm, companyPt);
    const notesLines = estimateNotesLines(notesCharCount, contentWidthMm, band.notesPt);

    const markHeightMm = logoWidthMm * 0.55 + (hasQr ? band.qrMm + 1.5 : 0);
    const metaHeightMm = Math.max(markHeightMm, metaLineCount * band.metaLineMm);
    const addrHeightMm = addressLineCount * band.addrLineMm + 1.2;
    const notesHeightMm = notesLines ? notesLines * band.notesLineMm + 1 : 0;
    const coldHeightMm = hasCold ? band.coldMm : 0;
    const sigsHeightMm = hasSigs ? band.sigsMm : 0;
    const companyHeightMm = companyLines
      ? companyLines * ptToMm(companyPt) * 1.2 + 1.4
      : 0;

    const blocks = [
      metaHeightMm,
      addrHeightMm,
      notesHeightMm,
      coldHeightMm,
      sigsHeightMm,
      companyHeightMm,
    ].filter((h) => h > 0);
    const gaps = Math.max(0, blocks.length - 1) * band.gapMm;
    const totalMm = blocks.reduce((sum, h) => sum + h, 0) + gaps;

    if (totalMm <= contentHeightMm) {
      return {
        key: band.key,
        companyPt,
        estimatedHeightMm: Math.round(totalMm * 10) / 10,
        contentHeightMm,
        contentWidthMm,
        ...band,
      };
    }
  }

  const fallback = CHEETAH_LZ17_LABEL_FIT_BANDS[CHEETAH_LZ17_LABEL_FIT_BANDS.length - 1];
  const companyPt = fitCheetahLabelCompanyPt(
    companyText,
    contentWidthMm,
    fallback.companyMaxPt,
    fallback.companyMinPt,
  );
  return {
    key: fallback.key,
    companyPt,
    estimatedHeightMm: contentHeightMm,
    contentHeightMm,
    contentWidthMm,
    ...fallback,
  };
}
